// components/reports/RiskTrendChart.js
// Risk Score Trend — Recharts LineChart showing weekly risk scores over time
// Uses useThemeColors() for all colors — no CSS var strings in SVG props

import React from 'react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid,
  Tooltip, ResponsiveContainer, ReferenceLine
} from 'recharts';
import { useThemeColors } from '../../hooks/useThemeColors';

function RiskTrendChart({ data = [] }) {
  const colors = useThemeColors();

  if (!data || data.length === 0) {
    return (
      <div className="trend-chart-card">
        <h3 className="trend-chart-title">Risk Score Trend</h3>
        <div className="trend-chart-empty">
          No trend data available yet.
        </div>
      </div>
    );
  }

  // Calculate summary stats
  const latest = data[data.length - 1];
  // PRD-51: higher = riskier — "best" is the LOWEST score
  const best = data.reduce((min, d) => d.riskScore < min.riskScore ? d : min, data[0]);
  // PRD-51: Determine trend direction (higher = riskier, so increase = Degrading)
  const firstScore = data[0]?.riskScore || 0;
  const lastScore = latest?.riskScore || 0;
  const trendLabel = lastScore < firstScore ? 'Improving' : lastScore > firstScore ? 'Degrading' : 'Stable';
  const trendArrow = lastScore < firstScore ? '\u2193' : lastScore > firstScore ? '\u2191' : '\u2192';
  // PRD-51: Dynamic Y-axis max
  const maxScore = Math.max(...data.map(d => d.riskScore || 0));
  const yAxisMax = maxScore > 100 ? Math.ceil(maxScore * 1.1 / 50) * 50 : 100;

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    const d = payload[0]?.payload;
    const prev = data.find((_, i) => i < data.indexOf(d)) || {};
    const delta = d.riskScore - (prev.riskScore || d.riskScore);

    return (
      <div style={{
        background: colors.bg.card,
        border: `1px solid ${colors.border.medium}`,
        borderRadius: '6px',
        padding: '10px 14px',
        fontSize: '13px'
      }}>
        <div style={{ color: colors.text.primary, fontWeight: 'bold', marginBottom: 4 }}>
          {d.label || label}
        </div>
        <div style={{ color: colors.accent.primary }}>
          Score: {d.riskScore}
        </div>
        {delta !== 0 && (
          <div style={{ color: delta > 0 ? colors.severity.critical : colors.severity.low, fontSize: '12px' }}>
            {delta > 0 ? '+' : ''}{delta} from previous
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="trend-chart-card">
      <div className="trend-chart-header">
        <h3 className="trend-chart-title">Risk Score Trend</h3>
        <span className="trend-chart-period">Last {data.length} weeks</span>
      </div>
      <div className="trend-chart-body">
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={data} margin={{ top: 10, right: 30, left: 0, bottom: 5 }}>
            <CartesianGrid strokeDasharray="3 3" stroke={colors.border.dark} />
            <XAxis
              dataKey="label"
              stroke={colors.text.muted}
              tick={{ fontSize: 12, fill: colors.text.muted }}
            />
            <YAxis
              domain={[0, yAxisMax]}
              stroke={colors.text.muted}
              tick={{ fontSize: 12, fill: colors.text.muted }}
            />
            <Tooltip content={<CustomTooltip />} />
            <ReferenceLine
              y={80}
              stroke={colors.severity.critical}
              strokeDasharray="5 5"
              strokeOpacity={0.4}
              label={{ value: 'Critical', fill: colors.text.muted, fontSize: 11 }}
            />
            <Line
              type="monotone"
              dataKey="riskScore"
              stroke={colors.accent.primary}
              strokeWidth={2}
              dot={{ fill: colors.accent.primary, r: 4 }}
              activeDot={{ r: 6, fill: colors.accent.primary }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="trend-chart-summary">
        <span className="trend-stat">
          Current: <strong>{latest?.riskScore || 0}</strong>
        </span>
        <span className="trend-stat" style={{ color: lastScore >= firstScore ? colors.severity.low : colors.severity.critical }}>
          Trend: {trendArrow} {trendLabel}
        </span>
        <span className="trend-stat">
          Best: <strong>{best?.riskScore || 0}</strong> ({best?.label || 'N/A'})
        </span>
      </div>
    </div>
  );
}

export default RiskTrendChart;
